import { Image } from "@tiptap/extension-image";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { MarkdownImageNodeView } from "./markdown-image-node-view";
import {
  extractAlignmentFromClassList,
  extractAlignmentFromStyle,
  serializeImageNodeToHtml,
} from "./markdown-image-serializer";

export interface MarkdownImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    markdownImage: {
      /**
       * Add an image with optional width, height, and alignment
       */
      setMarkdownImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: string;
        height?: string;
        alignment?: "left" | "center" | "right";
        style?: string;
      }) => ReturnType;
    };
  }
}

export const MarkdownImage = Image.extend<MarkdownImageOptions>({
  name: "image",

  addOptions() {
    return {
      ...this.parent?.(),
      inline: false,
      allowBase64: true,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      alignment: {
        default: "left",
        parseHTML: (element) => {
          return (
            element.getAttribute("data-align") ||
            extractAlignmentFromClassList(element.classList.value) ||
            extractAlignmentFromStyle(element.getAttribute("style")) ||
            "left"
          );
        },
        renderHTML: (attributes) => {
          return { "data-align": attributes.alignment };
        },
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setMarkdownImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt,
              title: options.title,
              width: options.width,
              height: options.height,
              alignment: options.alignment || "left",
              style: options.style,
            },
          });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownImageNodeView);
  },

  renderHTML({ HTMLAttributes }) {
    const { alignment, ...otherAttrs } = HTMLAttributes;
    console.log("Rendering HTML with attributes:", HTMLAttributes);
    console.log("this.options.HTMLAttributes:", this.options.HTMLAttributes);
    return [
      "img",
      {
        ...this.options.HTMLAttributes,
        ...otherAttrs,
        class: `markdown-image ${alignment ? `align-${alignment}` : "align-left"}`,
      },
    ];
  },

  renderMarkdown: (node) => {
    return serializeImageNodeToHtml(node.attrs || {});
  },
});
